import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { User, Mail, Phone, Shield, Key, Save, Camera, Check, BellRing, Link2, Copy, QrCode, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Profile = () => {
  const { user, setAuthData, token } = useAuth();
  const fileInputRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({
    nome: user?.nome || '',
    cognome: user?.cognome || '',
    telefono: user?.telefono || ''
  });
  const [emailData, setEmailData] = useState({ email: user?.email || '' });
  const [editingEmail, setEditingEmail] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '', newPassword: '', confirmPassword: ''
  });
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [uploading, setUploading] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/users/me/referral-link`);
        if (!cancelled) {
          setReferralLink(res.data.link);
          setReferralCode(res.data.referral_code);
        }
      } catch (e) {
        console.warn('Referral link fetch failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Link copiato negli appunti');
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error('Impossibile copiare');
    }
  };

  const handleShareLink = async () => {
    const shareData = {
      title: 'Quotazione gratuita bolletta',
      text: "Ciao! Carica la tua bolletta e scopri quanto puoi risparmiare in 30 secondi. Analisi automatica e senza impegno.",
      url: referralLink,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) { /* utente ha annullato */ }
    } else {
      handleCopyLink();
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      super_admin: 'Super Admin', admin: 'Admin / Manager',
      sub_agent: 'Sub-agente', back_office: 'Back Office', interno: 'Utente Interno'
    };
    return labels[role] || role;
  };

  const handleSaveProfile = async () => {
    try {
      await axios.put(`${API_URL}/api/profile/update`, editData);
      toast.success('Profilo aggiornato');
      setEditing(false);
      const updatedUser = { ...user, ...editData };
      setAuthData(token, updatedUser);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento del profilo');
    }
  };

  const handleChangeEmail = async () => {
    if (!emailData.email) return;
    try {
      const res = await axios.put(`${API_URL}/api/profile/email`, { email: emailData.email });
      toast.success('Email aggiornata');
      setEditingEmail(false);
      const updatedUser = { ...user, email: res.data.email };
      setAuthData(token, updatedUser);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento email');
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/profile/password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success('Password aggiornata con successo');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio password');
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/profile/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAvatarUrl(res.data.avatar_url);
      toast.success('Foto profilo aggiornata');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Il Mio Profilo</h1>
        <p className="text-slate-500 mt-1">Gestisci le informazioni del tuo account</p>
      </div>

      {/* Profile Info */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-5 h-5" /> Informazioni Personali
          </CardTitle>
          {!editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>Modifica</Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="relative group">
              {avatarUrl ? (
                <img src={`${API_URL}${avatarUrl}`} alt="Avatar" className="w-24 h-24 rounded-full object-cover border-2 border-slate-200" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold text-2xl">
                  {user?.nome?.[0]}{user?.cognome?.[0]}
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 w-24 h-24 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                disabled={uploading}
              >
                <Camera className="w-6 h-6 text-white" />
              </button>
              <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={handleAvatarUpload} />
            </div>

            <div className="flex-1">
              {editing ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome</Label>
                    <Input value={editData.nome} onChange={(e) => setEditData({...editData, nome: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Cognome</Label>
                    <Input value={editData.cognome} onChange={(e) => setEditData({...editData, cognome: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefono</Label>
                    <Input value={editData.telefono} onChange={(e) => setEditData({...editData, telefono: e.target.value})} />
                  </div>
                  <div className="col-span-2 flex gap-2 mt-2">
                    <Button onClick={handleSaveProfile}><Save className="w-4 h-4 mr-2" /> Salva</Button>
                    <Button variant="outline" onClick={() => setEditing(false)}>Annulla</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">{user?.nome} {user?.cognome}</h2>
                    <span className={`inline-flex mt-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                      user?.ruolo === 'super_admin' ? 'bg-violet-100 text-violet-700' :
                      user?.ruolo === 'admin' ? 'bg-sky-100 text-sky-700' :
                      user?.ruolo === 'sub_agent' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {getRoleLabel(user?.ruolo)}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />{user?.telefono || 'Non specificato'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="w-5 h-5" /> Indirizzo Email
          </CardTitle>
          {!editingEmail && (
            <Button variant="outline" size="sm" onClick={() => { setEmailData({ email: user?.email || '' }); setEditingEmail(true); }}>Modifica</Button>
          )}
        </CardHeader>
        <CardContent>
          {editingEmail ? (
            <div className="flex items-end gap-3 max-w-md">
              <div className="flex-1 space-y-2">
                <Label>Nuova Email</Label>
                <Input type="email" value={emailData.email} onChange={(e) => setEmailData({ email: e.target.value })} data-testid="profile-email-input" />
              </div>
              <Button onClick={handleChangeEmail} disabled={!emailData.email} data-testid="save-email-btn"><Check className="w-4 h-4 mr-2" /> Salva</Button>
              <Button variant="outline" onClick={() => setEditingEmail(false)}>Annulla</Button>
            </div>
          ) : (
            <p className="text-slate-700 flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" />{user?.email}</p>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Key className="w-5 h-5" /> Cambia Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-md space-y-4">
            <div className="space-y-2">
              <Label>Password Attuale</Label>
              <Input type="password" value={passwordData.currentPassword} onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})} placeholder="Inserisci la password attuale" data-testid="current-password" />
            </div>
            <div className="space-y-2">
              <Label>Nuova Password</Label>
              <Input type="password" value={passwordData.newPassword} onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})} placeholder="Minimo 6 caratteri" data-testid="new-password" />
            </div>
            <div className="space-y-2">
              <Label>Conferma Nuova Password</Label>
              <Input type="password" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})} placeholder="Ripeti la nuova password" data-testid="confirm-password" />
            </div>
            <Button onClick={handleChangePassword} disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword} data-testid="change-password-btn">
              Aggiorna Password
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5" /> Informazioni Account
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-500">Stato</p>
              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${user?.attivo ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {user?.attivo ? 'Attivo' : 'Disattivato'}
              </span>
            </div>
            <div>
              <p className="text-sm text-slate-500">Ruolo</p>
              <p className="text-sm font-medium">{getRoleLabel(user?.ruolo)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Link Quotazione (referral) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Link2 className="w-5 h-5 text-sky-600" /> Link Quotazione Clienti
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">
            Condividi questo link o QR con i tuoi clienti. Caricando una bolletta, otterranno una comparazione istantanea con le tue offerte e potranno sceglierne una: <b>arriva a te come lead in tempo reale con notifica push</b>.
          </p>
          {referralLink ? (
            <>
              <div className="flex flex-col sm:flex-row items-stretch gap-2">
                <div className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                  <Link2 className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="text-xs sm:text-sm font-mono truncate text-slate-700 dark:text-slate-200" data-testid="referral-link">
                    {referralLink}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCopyLink}
                    className="flex-1 sm:flex-initial"
                    data-testid="copy-referral-btn"
                  >
                    {copied ? <Check className="w-4 h-4 mr-2 text-emerald-600" /> : <Copy className="w-4 h-4 mr-2" />}
                    {copied ? 'Copiato' : 'Copia'}
                  </Button>
                  <Button
                    onClick={handleShareLink}
                    className="flex-1 sm:flex-initial bg-sky-600 hover:bg-sky-700"
                    data-testid="share-referral-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" /> Condividi
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setShowQr(v => !v)}
                  className="text-slate-600"
                  data-testid="toggle-qr-btn"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  {showQr ? 'Nascondi QR Code' : 'Mostra QR Code'}
                </Button>
                <a
                  href={referralLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-sky-600 hover:underline"
                  data-testid="open-referral-btn"
                >
                  Apri anteprima →
                </a>
              </div>
              {showQr && (
                <div className="flex flex-col items-center gap-3 py-4 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                  <QRCodeSVG
                    value={referralLink}
                    size={200}
                    level="M"
                    includeMargin={true}
                    data-testid="referral-qr"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 text-center px-4">
                    Il cliente inquadra il QR con la fotocamera del telefono e apre subito il tuo comparatore.
                  </p>
                  <p className="text-[10px] text-slate-400 font-mono">Codice: {referralCode}</p>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-500">Caricamento link...</p>
          )}
        </CardContent>
      </Card>

      {/* Notifiche Push */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BellRing className="w-5 h-5 text-indigo-600" /> Notifiche Push
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">
            Ricevi avvisi immediati sul telefono anche a schermo spento per: nuova pratica assegnata, cambio stato pratica, nuova nota interna, commissione attribuita, alert documento mancante.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
                    toast.error("Questo browser non supporta le notifiche push");
                    return;
                  }
                  const perm = await Notification.requestPermission();
                  if (perm !== 'granted') {
                    toast.error("Permesso notifiche negato dal browser");
                    return;
                  }
                  const reg = await navigator.serviceWorker.ready;
                  let sub = await reg.pushManager.getSubscription();
                  if (!sub) {
                    const keyRes = await axios.get(`${API_URL}/api/push/vapid-public-key`);
                    const raw = keyRes.data.public_key;
                    const padding = '='.repeat((4 - (raw.length % 4)) % 4);
                    const base64 = (raw + padding).replace(/-/g, '+').replace(/_/g, '/');
                    const bin = window.atob(base64);
                    const arr = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
                    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: arr });
                    const j = sub.toJSON();
                    await axios.post(`${API_URL}/api/push/subscribe`, {
                      endpoint: j.endpoint,
                      keys: { p256dh: j.keys.p256dh, auth: j.keys.auth },
                    });
                  }
                  await axios.post(`${API_URL}/api/push/test`);
                  toast.success("Notifica push di test inviata al tuo device");
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Errore invio notifica di test");
                }
              }}
              data-testid="test-push-btn"
            >
              <BellRing className="w-4 h-4 mr-2" /> Invia notifica di test
            </Button>
            <Button
              variant="ghost"
              className="text-red-600"
              onClick={async () => {
                try {
                  const reg = await navigator.serviceWorker.ready;
                  const sub = await reg.pushManager.getSubscription();
                  if (sub) {
                    const j = sub.toJSON();
                    await axios.post(`${API_URL}/api/push/unsubscribe`, { endpoint: j.endpoint });
                    await sub.unsubscribe();
                    toast.success("Notifiche push disattivate su questo device");
                  } else {
                    toast.info("Nessuna sottoscrizione push attiva");
                  }
                } catch (e) {
                  toast.error("Errore disattivazione");
                }
              }}
              data-testid="unsubscribe-push-btn"
            >
              Disattiva su questo device
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
